import { SxProps, Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";

const TOP_OFFSET = 88;
const SIDEBAR_WIDTH = 280;
/** TopNav can be 60px (scrolled) or 100px; use max so button always sits below */
const MOBILE_TOP_NAV_HEIGHT = 120;

// Same approach as share bar in BlogDetail: position fixed to viewport
const adminSideNavLayoutStyles: Record<string, SxProps<Theme>> = {
  root: {
    display: "flex",
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    overflowX: "hidden",
    overflowY: "visible",
  },
  sidebar: {
    height: "fit-content",
    width: SIDEBAR_WIDTH,
    display: "flex",
    flexDirection: "column",
    position: "fixed",
    left: 20,
    top: TOP_OFFSET,
    bottom: 0,
    zIndex: 20,
    py: 1,
    px: 0.5,
    borderRadius: "12px",
    background: (t) => alpha(t.palette.background.paper, 0.92),
    border: (t) => `1px solid ${alpha(t.palette.divider, 0.25)}`,
    boxShadow: (t) => `0 4px 20px ${alpha(t.palette.common.black, 0.08)}`,
    backdropFilter: "blur(12px)",
  },
  sidebarHeader: {
    p: 2.5,
    borderBottom: (t) => `1px solid ${alpha(t.palette.divider, 0.12)}`,
    display: "flex",
    alignItems: "center",
    gap: 1.5,
  },
  logoText: {
    fontWeight: 700,
    fontSize: "1.25rem",
    background: "linear-gradient(135deg, #f8481c 0%, #ff6b4a 100%)",
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  navList: {
    flex: 1,
    py: 2,
    px: 1.5,
    overflowY: "auto",
  },
  navItem: {
    borderRadius: 2,
    mb: 0.5,
    "&.Mui-selected": {
      bgcolor: (t) => alpha("#f8481c", 0.12),
      color: "#f8481c",
      "& .MuiListItemIcon-root": { color: "#f8481c" },
    },
  },
  /** Sidebar when inside mobile Drawer – no fixed positioning, full width, aligned list */
  sidebarInDrawer: {
    width: "100%",
    height: "100%",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    position: "relative",
    top: 0,
    left: 0,
    bottom: "auto",
    py: 0,
    px: 0,
    borderRadius: 0,
    border: "none",
    boxShadow: "none",
    background: (t) => t.palette.background.paper,
  },
  sidebarHeaderInDrawer: {
    flexShrink: 0,
    p: 2.5,
    borderBottom: (t) => `1px solid ${alpha(t.palette.divider, 0.2)}`,
  },
  navListInDrawer: {
    flex: 1,
    minHeight: 0,
    py: 2,
    px: 2,
    overflowY: "auto",
  },
  navItemInDrawer: {
    borderRadius: "10px",
    py: 1.5,
    px: 2,
    mb: 1,
    alignItems: "center",
    "& .MuiListItemText-primary": {
      fontSize: "1rem",
      fontWeight: 500,
    },
    "&.Mui-selected": {
      bgcolor: (t) => alpha("#f8481c", 0.12),
      color: "#f8481c",
      "& .MuiListItemIcon-root": { color: "#f8481c" },
    },
  },
  main: {
    flex: 1,
    minWidth: 0,
    maxWidth: "100%",
    overflowX: "hidden",
    overflowY: "visible",
    display: "flex",
    flexDirection: "column",
    marginLeft: { xs: 0, lg: "300px" },
  },
  /** Sticky wrapper for mobile menu button – below TopNav, left-aligned, attractive pill */
  mobileMenuButtonWrapper: {
    display: { xs: "flex", lg: "none" },
    position: "fixed",
    top: MOBILE_TOP_NAV_HEIGHT,
    left: 0,
    zIndex: 19,
    p: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  mobileMenuButtonPill: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    borderRadius: "0 12px 12px 0",
    background: (t) => alpha(t.palette.background.paper, 0.98),
    border: (t) => `1px solid ${alpha(t.palette.divider, 0.2)}`,
    borderLeft: "none",
    boxShadow: (t) =>
      t.palette.mode === "dark"
        ? `0 4px 20px ${alpha("#000", 0.35)}`
        : `0 4px 20px ${alpha("#000", 0.1)}`,
    backdropFilter: "blur(12px)",
    transition: "all 0.2s ease",
    cursor: "pointer",
    font: "inherit",
    "&:hover": {
      boxShadow: (t) =>
        t.palette.mode === "dark"
          ? `0 6px 24px ${alpha("#000", 0.4)}`
          : `0 6px 24px ${alpha("#000", 0.12)}`,
      background: (t) => alpha(t.palette.background.paper, 1),
    },
  },
  mobileMenuButton: {
    color: "#f8481c",
    "&:hover": {
      bgcolor: (t) => alpha("#f8481c", 0.08),
      color: "#f8481c",
    },
  },
};

export default adminSideNavLayoutStyles;
export { SIDEBAR_WIDTH, TOP_OFFSET };
